// Formatting for the public property listing page (86bbrwd3v), following Rich's mockup.
// Pure so it can be unit tested.

export const DEFAULT_PROPERTY_IMAGE = '/default-property-image.jpg'

export const HIGHLIGHT_AMENITY_COUNT = 4

const NEARBY_ICONS = {
    school: '🏫',
    grocery_store: '🛒',
    park: '🌲',
}

const formatNumber = (value) => Number(value).toLocaleString('en-US', { maximumFractionDigits: 1 })

// Gallery photo URLs: uploaded photos in order, else the legacy cover photo, else the placeholder.
export function galleryPhotoUrls(property) {
    const urls = (property?.photos || []).map((photo) => photo.url).filter(Boolean)
    if (urls.length) {
        return urls
    }

    return [property?.photo || DEFAULT_PROPERTY_IMAGE]
}

export const hasUploadedPhotos = (property) => (property?.photos || []).some((photo) => photo.url)

// "$1,395" — cents only when there are any.
export const formatRent = (rentCurrency) => `$${String(rentCurrency ?? '0').replace(/\.00$/, '')}`

export const formatMoney = (amount) => `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`

export const formatLeaseTerm = (months) => (months ? `${months} ${Number(months) === 1 ? 'month' : 'months'}` : null)

// The icon row under the address: "3 bed", "2 bath", "1,320 sqft".
export function headerFacts(property) {
    const facts = []
    if (property?.beds !== null && property?.beds !== undefined) {
        facts.push(property.beds === 0 ? { icon: 'bed', value: 'Studio', unit: '' } : { icon: 'bed', value: String(property.beds), unit: 'bed' })
    }
    if (property?.baths) {
        facts.push({ icon: 'bath', value: formatNumber(property.baths), unit: 'bath' })
    }
    if (property?.sq_ft) {
        facts.push({ icon: 'sqft', value: formatNumber(property.sq_ft), unit: 'sqft' })
    }

    return facts
}

// The availability line, split so the date can be emphasised: "Available **Oct 1**" in the header,
// "Available **October 1**" in the apply card. A listing that isn't taking applications reads
// "Not Available" instead, because its stored date says nothing about when it reopens.
export function availability(property, { length = 'short', inactive = false } = {}) {
    if (inactive) {
        return { prefix: '', value: 'Not Available' }
    }

    if (property?.is_available_now) {
        return { prefix: 'Available', value: 'now' }
    }

    const date = (length === 'long' ? property?.available_on_long : property?.available_on_short) || null

    return date === null ? null : { prefix: 'Available', value: date }
}

// The two grey boxes under "Facts & features"; rows without a value and empty boxes are left out.
export function factBoxes(property) {
    const boxes = [
        {
            title: 'Interior',
            rows: [
                ['Bedrooms', property?.beds ?? null],
                ['Bathrooms', property?.baths ? formatNumber(property.baths) : null],
            ],
        },
        {
            title: 'Property',
            rows: [
                ['Type', property?.property_structure_type_label || null],
            ],
        },
    ]

    return boxes
        .map((box) => ({ ...box, rows: box.rows.filter(([, value]) => value !== null && value !== '') }))
        .filter((box) => box.rows.length)
}

export const highlightAmenities = (property) => (property?.amenities || []).slice(0, HIGHLIGHT_AMENITY_COUNT)

export const directionsUrl = (property) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(property?.full_address || '')}`

export const formatDistance = (miles) => `${Number(miles) < 0.1 ? '< 0.1' : formatNumber(miles)} mi`

export const nearbyIcon = (type) => NEARBY_ICONS[type] || '📍'
